<script setup lang="ts">
import { ref } from "vue";
import Icon from "./Icon.vue";
import { api, type ConnectionTest, type Server } from "../api";
import ServerForm from "./ServerForm.vue";

defineProps<{ servers: Server[] }>();
const emit = defineEmits<{ changed: [] }>();

const adding = ref(false);
/** Id edytowanego serwera — naraz otwarty jest tylko jeden formularz. */
const editingId = ref<string | null>(null);

function edit(s: Server) {
  adding.value = false;
  editingId.value = editingId.value === s.id ? null : s.id;
}
const tests = ref<Record<string, ConnectionTest | { pending: true } | undefined>>({});

function isPending(t: unknown): t is { pending: true } {
  return !!t && (t as { pending?: boolean }).pending === true;
}

// Nieudany test to normalny wynik, nie awaria — pokazujemy go przy serwerze,
// żeby dało się poprawić dane i kliknąć jeszcze raz, bez zamykania alertu.
async function test(s: Server) {
  tests.value = { ...tests.value, [s.id]: { pending: true } };
  try {
    const r = await api.post<ConnectionTest>(`/api/servers/${encodeURIComponent(s.id)}/test`);
    tests.value = { ...tests.value, [s.id]: r };
  } catch (e) {
    tests.value = {
      ...tests.value,
      [s.id]: { ok: false, message: (e as Error).message, details: {} },
    };
  }
}

// Usuwania serwerów celowo nie ma: rekord jest potrzebny do cofania napraw
// i rozliczeń. Zmiana hasła/klucza idzie przez „Edytuj”.

function fmt(v: string | number | boolean | null): string {
  if (v === true) return "tak";
  if (v === false) return "nie";
  if (v === null) return "—";
  return String(v);
}
</script>

<template>
  <div class="card">
    <h2>
      Twoje serwery
      <span class="spacer" />
      <button type="button" class="link" @click="adding = !adding; editingId = null">
        <template v-if="adding">anuluj</template><template v-else><Icon name="plus" /> dodaj</template>
      </button>
    </h2>

    <div class="body">
      <div v-if="!servers.length && !adding" class="empty">
        <div class="big"><Icon name="server" :size="40" /></div>
        <p class="lead">Brak serwerów</p>
        <p class="sub">Dodaj pierwszy, żeby uruchomić skan.</p>
        <button type="button" @click="adding = true"><Icon name="plus" /> Dodaj serwer</button>
      </div>

      <div v-for="s in servers" :key="s.id">
        <div class="srv">
          <div class="grow">
          <div class="name">{{ s.label }}</div>
          <div class="meta">
            {{ s.protocol }}://{{ s.username }}@{{ s.host }}:{{ s.port }}
            <template v-if="s.secure"> (FTPS)</template>
            • {{ s.authKind === "key" ? "klucz" : "hasło" }}
          </div>

          <div
            v-if="tests[s.id]"
            class="testRes"
            :class="isPending(tests[s.id]) ? '' : (tests[s.id] as ConnectionTest).ok ? 'ok' : 'bad'"
          >
            <template v-if="isPending(tests[s.id])">Łączę z {{ s.host }}…</template>
            <template v-else>
              <div class="testMsg">
                <Icon :name="(tests[s.id] as ConnectionTest).ok ? 'check' : 'x'" />
                {{ (tests[s.id] as ConnectionTest).message }}
              </div>
              <div
                v-for="(v, k) in (tests[s.id] as ConnectionTest).details"
                :key="k"
                class="testDet"
              >
                {{ k }}: {{ fmt(v) }}
              </div>
            </template>
          </div>
        </div>

        <button type="button" class="sec small" :disabled="isPending(tests[s.id])" @click="test(s)">
          Testuj
        </button>
        <button type="button" class="sec small" @click="edit(s)">
          {{ editingId === s.id ? "Anuluj" : "Edytuj" }}
        </button>
        </div>

        <ServerForm
          v-if="editingId === s.id"
          :server="s"
          @saved="editingId = null; emit('changed')"
          @cancel="editingId = null"
        />
      </div>

      <ServerForm
        v-if="adding"
        @saved="adding = false; emit('changed')"
        @cancel="adding = false"
      />
    </div>
  </div>
</template>

<style scoped>
.empty .big { font-size: 30px; opacity: .55; margin-bottom: var(--s2); }
.empty .lead { color: var(--fg); font-weight: 600; margin: 0 0 var(--s1); }
.empty .sub { margin: 0 0 var(--s4); font-size: 12px; }
.srv {
  border: 1px solid var(--bd); border-radius: var(--r2); padding: var(--s3);
  background: var(--panel2); display: flex; align-items: flex-start; gap: var(--s3);
  margin-bottom: var(--s2);
}
.name { font-weight: 650; font-size: 13px; }
.meta { font-size: 12px; color: var(--mut); word-break: break-all; margin-top: 2px; }
.testRes {
  margin-top: var(--s2); font-size: 12px; border-radius: var(--r1);
  padding: var(--s2) var(--s3); background: var(--panel3);
}
.testRes.ok { background: var(--ok-dim); }
.testRes.bad { background: var(--crit-dim); }
.testMsg { font-weight: 600; margin-bottom: var(--s1); }
.testRes.ok .testMsg { color: var(--ok); }
.testRes.bad .testMsg { color: var(--crit); }
.testDet { color: var(--mut); font-family: var(--mono); font-size: 11px; }
</style>
